<template>
  <div class="container">
    <o-bread-crumb>
      <template v-slot:pageName>Voucher</template>
      <template v-slot:pageDetail>Gift Voucher</template>
    </o-bread-crumb>

    <div v-if="validation" class="alert alert-danger mt-1" role="alert">
      {{ validation }}
    </div>

    <FeatureGate featureKey="voucher_gift">
    <div v-if="thisComponentShow" class="card text-center bg-white shadow-none">
      <div class="card-header">
        <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
          <li class="nav-item">
            <div class="header-content-left text-start">
              <h1 class="header-title">Gift Voucher</h1>
              <p class="header-subtitle">Gift voucher to members</p>
            </div>
          </li>
        </ul>
      </div>
      <div class="card-body">
        <div class="spinner d-flex justify-content-center align-items-center mb-4 mt-4" v-if='page.isLoading'>
          <div class="spinner-border text-primary" role="status"></div>
        </div>

        <div v-if="page.output">
          <div class="filter-action-wrapper mb-4 mt-3">
            <div class="filter-controls">
              <!-- Search Box -->
              <div class="search-box-custom">
                <div class="search-type-tabs">
                  <button 
                    v-for="opt in searchOptions" 
                    :key="opt.value"
                    :class="['tab-btn', { active: searchSelected === opt.value }]"
                    @click="searchSelected = opt.value"
                  >
                    {{ opt.text }}
                  </button>
                </div>
                <div class="search-input-wrapper">
                  <input 
                    type="text" 
                    class="form-control-search"
                    v-model="searchCriteria" 
                    @keyup.enter="doSearch"
                    :placeholder="'Search by ' + (searchOptions.find(o => o.value === searchSelected)?.text || '...')" 
                  />
                  <button v-if="!isSearchActive" class="btn-search" @click="doSearch">
                    <i class="fas fa-search"></i>
                  </button>
                  <button v-else class="btn-search" @click="clearSearch">
                    <i class="fas fa-times text-danger"></i>
                  </button>
                </div>
              </div>

              <!-- Status Filter -->
              <div class="filter-section">
                <div class="section-label-tabs">
                  <span class="label-tab">Status</span>
                </div>
                <div class="date-range-box">
                  <div class="status-filter-box">
                    <select class="form-select form-control-filter" v-model="filterStatus" @change="applyFilter">
                      <option value="">All Status</option>
                      <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div class="action-section">
              <div class="action-buttons-group">
                <button class="btn-refresh" @click="loadState">
                  <i class="fas fa-sync-alt"></i>
                </button>
                <router-link :to="{ name: 'VoucherGiftHistory' }" class="btn-action-secondary">
                  <i class="fas fa-arrow-left me-1"></i> Back to History
                </router-link>
              </div>
            </div>
          </div>

          <o-table style="text-align: left" class="table-hover member-list-table">
            <thead>
              <tr>
                <td class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                  {{ field }}
                </td>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in vouchers" :key="index" class="member-row-clickable">
                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.point }}</td>
                <td>{{ item.is_limited ? item.stock : 'Unlimited' }}</td>
                <td>{{ item.expiration || '-' }}</td>
                <td>
                  <span class="status-badge" :class="getVoucherStatusClass(item)">
                    {{ getVoucherStatus(item) }}
                  </span>
                </td>
                <td>
                  <span
                    class="d-inline-block"
                    :title="getVoucherStatus(item) !== 'Active' ? 'This voucher cannot be gifted' : ''"
                    :style="getVoucherStatus(item) !== 'Active' ? 'cursor: not-allowed;' : ''"
                  >
                    <button
                      class="btn btn-action btn-info-action"
                      @click="openGiftModal(item)"
                      :disabled="getVoucherStatus(item) !== 'Active'"
                      :style="getVoucherStatus(item) !== 'Active' ? 'pointer-events: none;' : ''"
                    >
                      <i class="fas fa-gift me-1"></i> Gift
                    </button>
                  </span>
                </td>
              </tr>
              <tr v-if="vouchers.length === 0">
                <td colspan="7" class="text-center">No vouchers available</td>
              </tr>
            </tbody>
          </o-table>
        </div>
      </div>
      <div class="container mt-3 mb-4" v-if="page.output">
        <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
      </div>
    </div>

    <GiftVoucherModal
      :isVisible="showModal"
      :voucher="selectedVoucher"
      @close="closeModal"
      @gift-success="onGiftSuccess"
    />
    </FeatureGate>
  </div>
</template>

<script>
import store from '@/store/'
import { OTable, OPagination, OBreadCrumb } from '@/components/OmnihControl'
import FeatureGate from '@/components/upsell/FeatureGate.vue'
import { computed, onMounted, reactive, ref, watch, onBeforeUnmount } from 'vue'
import GiftVoucherModal from '@/components/voucher/GiftVoucherModal.vue'
import { useToast } from '@/composables/useToast'

export default {
  components: { OTable, OPagination, OBreadCrumb, GiftVoucherModal, FeatureGate },

  setup() {
    const { showToast } = useToast()

    const isVoucherGiftEnabled = computed(() => {
        const flag = store.getters['FeatureFlag/getVoucherGiftFlag'];
        return flag !== 'NONACTIVE';
    });

    const meta = computed(() => store.state.Voucher.meta)
    const allVouchers = computed(() => store.state.Voucher.vouchers)
    const page = reactive({ isLoading: false, output: false })
    const validation = ref('')
    const showModal = ref(false)
    const selectedVoucher = ref(null)
    const selfLoaded = ref(store.state.Auth.selfLoaded)
    const thisComponentShow = ref(true)

    const fields = ref(['No', 'Name', 'Point', 'Stock', 'Expired At', 'Status', 'Action'])

    // Search state
    const searchSelected = ref('name')
    const searchCriteria = ref('')
    const isSearchActive = ref(false)
    const filterStatus = ref('')
    const searchOptions = reactive([
      { value: 'name', text: 'Name' },
    ])

    const statusOptions = reactive([
      { value: 'Active', text: 'Active' },
      { value: 'Expired', text: 'Expired' },
      { value: 'Out of Stock', text: 'Out of Stock' },
    ])

    const vouchers = computed(() => {
      return allVouchers.value
    })

    watch(
      () => store.state.Auth.selfLoaded,
      (newVal) => { if (newVal) { selfLoaded.value = newVal; loadState() } }
    )

    onMounted(() => {
      store.commit('Voucher/reset_meta')
      store.commit('Voucher/clear_search')
      store.commit('Voucher/clear_filter')
      if (selfLoaded.value) loadState()
    })

    onBeforeUnmount(() => {
      store.commit('Voucher/clear_search')
      store.commit('Voucher/clear_filter')
    })

    function loadState() {
      page.isLoading = true
      validation.value = ''
      
      const merchantId = store.state.Auth.selectedBusinessProfile?.merchant_id;
      if (merchantId) {
        store.commit('Voucher/add_filter', { filter: { merchant_id: merchantId } })
      }

      store.dispatch('Voucher/read')
        .catch((err) => { 
          let msg = err?.response?.data?.message || 'Failed to load vouchers'
          if (typeof msg !== 'string') msg = JSON.stringify(msg)
          validation.value = msg
          thisComponentShow.value = false
        })
        .finally(() => { page.isLoading = false; page.output = true })
    }

    function applySearchAndFilter() {
      let search = {}
      if (isSearchActive.value && searchCriteria.value) {
        search[searchSelected.value] = searchCriteria.value
      }
      if (filterStatus.value) {
        search.status = filterStatus.value
      }

      if (Object.keys(search).length === 0) {
        store.commit('Voucher/clear_search')
      } else {
        store.commit('Voucher/set_search', { search })
      }
      
      loadState()
    }

    function doSearch() {
      if (!searchCriteria.value) {
        clearSearch()
        return
      }

      isSearchActive.value = true

      if (meta.value.page !== 1) {
        const updatedMeta = { ...meta.value, page: 1 }
        store.commit('Voucher/switch_page', { meta: updatedMeta })
      }

      applySearchAndFilter()
    }

    function clearSearch() {
      isSearchActive.value = false
      searchCriteria.value = ''
      
      const updatedMeta = { ...meta.value, page: 1 }
      store.commit('Voucher/switch_page', { meta: updatedMeta })
      
      applySearchAndFilter()
    }

    function applyFilter() {
      store.commit('Voucher/switch_page', { meta: { ...meta.value, page: 1 } })
      applySearchAndFilter()
    }

    function navPage(p) {
      const _meta = { ...meta.value, page: p }
      store.commit('Voucher/switch_page', { meta: _meta })
      loadState()
    }

    function openGiftModal(voucher) {
      selectedVoucher.value = voucher
      showModal.value = true
    }

    function closeModal() {
      showModal.value = false
      selectedVoucher.value = null
    }

    function onGiftSuccess(result) {
      const voucherName = selectedVoucher.value?.name || ''
      closeModal()
      showToast({
        type: 'success',
        title: 'Success',
        message: `Voucher "${voucherName}" sedang diproses untuk ${result.total_sent} member.`
      })
    }

    function getVoucherStatus(item) {
      if (!item.expiration) return 'Inactive'
      const expDate = new Date(item.expiration.replace(' ', 'T'))
      const now = new Date()
      if (expDate <= now) return 'Expired'
      if (item.is_limited && parseInt(item.stock) <= 0) return 'Out of Stock'
      return 'Active'
    }

    function getVoucherStatusClass(item) {
      const status = getVoucherStatus(item)
      const map = {
        'Active': 'status-active',
        'Expired': 'status-expired',
        'Out of Stock': 'status-out-of-stock',
        'Inactive': 'status-inactive'
      }
      return map[status] || 'status-inactive'
    }

    return { 
      page, fields, meta, vouchers, validation, showModal, selectedVoucher, 
      navPage, openGiftModal, closeModal, onGiftSuccess, getVoucherStatus, getVoucherStatusClass,
      searchSelected, searchCriteria, isSearchActive, searchOptions, statusOptions,
      filterStatus, doSearch, clearSearch, applyFilter, loadState, thisComponentShow,
      isVoucherGiftEnabled
    }
  }
}
</script>

<style scoped>
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.filter-section, .action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 350px;
    min-width: 250px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
    flex-wrap: wrap;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #0d6efd);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    transition: all 0.2s;
    overflow: hidden;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px var(--secondary-color, rgba(13, 110, 253, 0.1));
}

.search-input-wrapper input {
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
    box-shadow: none;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #0d6efd);
}

.search-input-wrapper .btn-search {
    position: relative;
    width: 44px;
    height: 42px;
    border-radius: 0;
    background: transparent;
}

.search-input-wrapper .btn-search:hover {
    background: #f1f5f9;
}

.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px;
    min-width: 150px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px var(--secondary-color, rgba(13, 110, 253, 0.1));
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-action-secondary {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 600;
    font-size: 13px;
    padding: 0 16px;
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
    cursor: pointer;
}

.btn-action-secondary:hover {
    background-color: #e2e8f0;
    color: #1e293b;
}

.member-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.member-list-table th,
.member-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.member-row-clickable {
    transition: background-color 0.2s;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.btn-action {
    padding: 8px 18px; /* Slightly larger padding for prominence */
    font-size: 13px; /* Slightly larger text */
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-info-action {
    background-color: var(--primary-color, #0d6efd); /* Prominent primary color */
    color: #ffffff;
    box-shadow: 0 4px 6px var(--secondary-color, rgba(13, 110, 253, 0.2));
}

.btn-info-action:hover:not(:disabled) {
    background-color: var(--primary-color, #0b5ed7); /* Darker primary on hover */
    filter: brightness(85%);
    color: #ffffff;
    box-shadow: 0 6px 10px var(--secondary-color, rgba(13, 110, 253, 0.3));
    transform: translateY(-1px);
}

.btn-info-action:disabled {
    background-color: #cbd5e1;
    color: #ffffff;
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.8;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    text-align: left;

}

/* Spinner Overlay */
.spinner {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    z-index: 200;
}

/* Status Badges */
.status-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    display: inline-block;
}

.status-badge.status-active {
    background-color: #f0fdf4;
    color: #166534;
}

.status-badge.status-expired {
    background-color: #fff1f2;
    color: #9f1239;
}

.status-badge.status-out-of-stock {
    background-color: #fff7ed;
    color: #9a3412;
}

.status-badge.status-inactive {
    background-color: #f1f5f9;
    color: #475569;
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}
</style>
